import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router, Redirect } from 'react-router';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import * as serviceWorker from './serviceWorker';
import { createBrowserHistory } from 'history';
import {
  PUBLIC_PATH,
  APP_ROUTES,
  SETTINGS_ROUTES,
  GES_PERMITTED_ROLES,
  CX_QUEUE_PERMITTED_ROLES
} from './shared/config/routes-config';
import ReduxStore from './shared/redux/store';
import ReactNotification from 'react-notifications-component';

// import global styles
import './index.scss';
import './styles.css';
import 'react-notifications-component/dist/theme.css';
// import tracking section
import { initializeTrackingTools } from './shared/services/utils/tracking/tracking.service';
// import i18n config
import { initializeI18n } from './shared/config/i18n.config';
import { AuthModule } from './AuthModule/auth.module';
import { refreshUserSession, getUserNotification } from './shared/services/data/auth.data';
import DefaultLayout from './shared/layouts/default/default.layout';
import BlankLayout from './shared/layouts/blank/blank.layout';
import { AreaModule } from './AreaModule/area.module';
import { PropertyModule } from './PropertyModule/property.module';
import { AmenityModule } from './AmenityModule/amenity.module';
import { UnitTypeModule } from './UnitTypeModule/unit-type.module';
import { BookingModule } from './BookingModule/booking.module';
import { BlockDatesModule } from './BlockDatesModule/block-dates.module';
import { DashboardModule } from './DashboardModule/dashboard.module';
import { ReservationModule } from './ReservationModule/reservation.module';
import { CompanyModule } from './CompanyModule/company.module';
import { UnitTypeGroupModule } from './unitTypeGroupModule/unit-type-group.module';
import { AuthStatus } from './shared/enums/auth.status.enum';
import { RouteConfig } from './shared/interfaces/routes-config.interface';
import { UserRole } from './shared/enums/user-role.enum';
import { PaymentsModule } from './paymentsModule/payments.module';
import { UserModule } from './UserModule/user.module';
import { ConfigurationModule } from './ConfigurationModule/configuration.module';
import { ReportsModule } from './ReportsModule/reports.module';
import { ExpensesModule } from './ExpensesModule/expenses.module';
import { PayoutModule } from './PayoutModule/payout.module';
// import { GuestManagementModule } from './GuestManagementModule/guestManagement.module';
import { UserManagementModule } from './UserManagementModule/userManagement.module';
import { KpisModule } from './KpisModule/KpisModule.module';
import { FurnishingManagementModule } from './FurnishingModule/components/furnishing.module';
import { FurnishingRequestsModule } from './FurnishingModule/components/FurnishingRequests/FurnishingRequests.module';
import { Toaster } from 'sonner';
import { DailyOperationsModule } from './DailyOperationsModule/DailyOperationsModule';
import { OperationModule } from './OperationModule/OperationModule';
import { HouseKeepingModule } from './HouseKeepingModule/HouseKeepingModule';
import { OccupancyRateModule } from './OccupancyRateModule/OccupancyRateModule';
import { GESModule } from './GESModule/GESModule';
import { CXQueueModule } from './CXQueueModule/CXQueueModule';
import { OperationMaintenanceModule } from './OperationModule/OperationMaintenanceModule';

// import app modules

const store = ReduxStore;
initializeTrackingTools();
initializeI18n();
const history = createBrowserHistory({ basename: PUBLIC_PATH });
// refresh user token and check validity
refreshUserSession().then((response) => {
  if (
    response === AuthStatus.UNAUTHORIZED &&
    window.location.href.indexOf(
      (APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH
    ) < 0
  ) {
    console.log('User not premitted to access admin page');
    window.location.href =
      PUBLIC_PATH + (APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH;
    // setTimeout(() => {
    //   history.push((APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH);
    // }, 500);
  } else {
    getUserNotification().then();
  }
});
ReactDOM.render(
  <>
    <ReactNotification />
    <Toaster
      position="top-center"
      richColors
      toastOptions={{
        classNames: { title: '!font-bold !text-xl !text-neutral-700 !font-gilroy-medium' }
      }}
    />
    <Provider store={store}>
      <Router history={history}>
        <BrowserRouter basename={PUBLIC_PATH}>
          <Switch>
            <Route exact={true} path="/">
              <Redirect to="/home" />
            </Route>
            <Route exact={true} path="/home">
              <Redirect to={APP_ROUTES.DASHBOARD_MODULE.PATH} />
            </Route>
            <BlankLayout
              authenticationRequired={false}
              component={AuthModule}
              path={APP_ROUTES.AUTH_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={AreaModule}
              path={APP_ROUTES.AREA_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={UnitTypeGroupModule}
              path={APP_ROUTES.UNIT_TYPE_GROUP_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={AmenityModule}
              path={APP_ROUTES.AMENITY_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={PropertyModule}
              path={APP_ROUTES.PROPERTY_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.RESERVATION_AGENT,
                UserRole.RESERVATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.COMMERCIAL_MANAGER,
                UserRole.RESERVATIONS,
                UserRole.HOSPITALITY_AGENT,
                UserRole.GES,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.FINANCE_MANAGER,
                UserRole.FINANCE_SPECIALIST,
                UserRole.OPERATION_MANAGER,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.SALES_SPECIALIST,
                UserRole.SALES_MANAGER
              ]}
              component={ReservationModule}
              path={APP_ROUTES.RESERVATION_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.RESERVATION_MANAGER,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.OPERATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={UnitTypeModule}
              path={APP_ROUTES.UNIT_TYPE_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.FINANCE_MANAGER,
                UserRole.FINANCE_SPECIALIST,
                UserRole.OPERATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={ExpensesModule}
              path={APP_ROUTES.EXPENSES_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.FINANCE_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.FINANCE_SPECIALIST
              ]}
              component={PayoutModule}
              path={APP_ROUTES.PAYOUT_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.RESERVATION_AGENT,
                UserRole.RESERVATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.COMMERCIAL_MANAGER,
                UserRole.RESERVATIONS
              ]}
              component={BookingModule}
              path={APP_ROUTES.BOOKING_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.RESERVATION_AGENT,
                UserRole.RESERVATION_MANAGER,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.RESERVATIONS,
                UserRole.OPERATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ]}
              component={BlockDatesModule}
              path={APP_ROUTES.BLOCK_DATES_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.RESERVATIONS]}
              component={CompanyModule}
              path={APP_ROUTES.COMPANY_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.SALES_SPECIALIST,
                UserRole.SALES_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.FINANCE_MANAGER,
                UserRole.FINANCE_SPECIALIST,
                UserRole.RESERVATION_MANAGER,
                UserRole.RESERVATION_AGENT,
                UserRole.RESERVATIONS,
                UserRole.HOSPITALITY_AGENT,
                UserRole.GES,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.HOUSE_KEEPING_MANAGER,
                UserRole.OPERATION_MANAGER,
                UserRole.COMMERCIAL_MANAGER,
                UserRole.INVESTOR
              ]}
              component={DashboardModule}
              path={APP_ROUTES.DASHBOARD_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.OPERATION_MANAGER,
                // UserRole.RESERVATION_MANAGER,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.FINANCE_MANAGER,
                UserRole.INVESTOR,
                UserRole.COMMERCIAL_MANAGER
              ]}
              component={KpisModule}
              path={APP_ROUTES.KPIS_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.FINANCE_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.FINANCE_SPECIALIST,
                UserRole.RESERVATION_MANAGER,
                UserRole.RESERVATIONS,
                UserRole.OPERATION_MANAGER
              ]}
              component={PaymentsModule}
              path={APP_ROUTES.PAYMENTS_MODULE.PATH}
            />
            {/* <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.FINANCE_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.FINANCE_SPECIALIST
              ]}
              component={ReportsModule}
              path={APP_ROUTES.REPORTS_MODULE.PATH}
            /> */}
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.E_COMMERCE,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.SALES_SPECIALIST,
                UserRole.SALES_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN,
                UserRole.RESERVATIONS,
                UserRole.RESERVATION_MANAGER,
                UserRole.RESERVATION_AGENT,
                UserRole.HOSPITALITY_MANAGER,
                UserRole.HOSPITALITY_AGENT,
                UserRole.GES,
                UserRole.OPERATION_MANAGER,
                UserRole.COMMERCIAL_MANAGER
              ]}
              component={UserModule}
              path={APP_ROUTES.USER_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[UserRole.SUPER_ADMIN]}
              component={UserManagementModule}
              path={APP_ROUTES.USER_MANAGEMENT_MODULE.PATH}
            />
            {/* <DefaultLayout
              authenticationRequired={true}
              permittedList={[UserRole.SUPER_ADMIN]}
              component={GuestManagementModule}
              path={APP_ROUTES.GUEST_MANAGEMENT_MODULE.PATH}
            /> */}
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              component={ConfigurationModule}
              path={SETTINGS_ROUTES.ACCOUNT_CONFIGURATION_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN,
                UserRole.ADMIN,
                UserRole.DESIGN_MANAGER,
                UserRole.DESIGN_TEAM
              ]}
              component={FurnishingManagementModule}
              path={APP_ROUTES.FURNISHING_MANAGEMENT_MODULE.CHILDREN?.PACKAGES.FULL_PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN,
                UserRole.ADMIN,
                UserRole.DESIGN_TEAM,
                UserRole.DESIGN_MANAGER,
                UserRole.SALES_SPECIALIST,
                UserRole.SALES_MANAGER
              ]}
              component={FurnishingRequestsModule}
              path={APP_ROUTES.FURNISHING_MANAGEMENT_MODULE.CHILDREN?.REQUESTS.FULL_PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.OPERATION_MANAGER]}
              component={DailyOperationsModule}
              path={APP_ROUTES.DAILY_OPERATIONS_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN,
                UserRole.OPERATION_MANAGER,
                UserRole.HOUSE_KEEPING_MANAGER
                // UserRole.ADMIN,
                // UserRole.HOSPITALITY_MANAGER,
                // UserRole.GES,
                // UserRole.HOSPITALITY_AGENT
              ]}
              component={OperationModule}
              path={APP_ROUTES.OPERATION_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN
                // , UserRole.RESERVATIONS, UserRole.ADMIN
              ]}
              component={OperationMaintenanceModule}
              path={APP_ROUTES.OPERATION_MAINTENANCE_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN,
                UserRole.ADMIN,
                UserRole.SALES_SPECIALIST,
                UserRole.ECOMMERCE_SENIOR_SPECIALIST,
                UserRole.ECOMMERCE_SPECIALIST,
                UserRole.SALES_MANAGER,
                UserRole.OPERATION_MANAGER
              ]}
              component={OccupancyRateModule}
              path={APP_ROUTES.OCCUPANCY_RATE_MODULE.PATH}
            />

            <DefaultLayout
              authenticationRequired={true}
              permittedList={[
                UserRole.SUPER_ADMIN,
                // UserRole.ADMIN,
                UserRole.OPERATION_MANAGER
                // UserRole.HOUSE_KEEPING,
                // UserRole.HOUSE_KEEPING_MANAGER
              ]}
              component={HouseKeepingModule}
              path={APP_ROUTES.HOUSE_KEEPING_MODULE.PATH}
            />

            <DefaultLayout
              authenticationRequired={true}
              permittedList={GES_PERMITTED_ROLES}
              component={GESModule}
              path={APP_ROUTES.GES_MODULE.PATH}
            />
            <DefaultLayout
              authenticationRequired={true}
              permittedList={CX_QUEUE_PERMITTED_ROLES}
              component={CXQueueModule}
              path={APP_ROUTES.CX_QUEUE_MODULE.PATH}
            />
          </Switch>
        </BrowserRouter>
      </Router>
    </Provider>
  </>,
  document.getElementById('root')
);

serviceWorker.unregister();
